import FroalaTextAreaEdit from '@/components/Froala/FroalaTextAreaEdit';
import { LessonView } from '@/components/LessonView';
import { Tag } from '@teo/components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type SanitizeReviewViewMode = 'preview' | 'raw' | 'editor';

const PREVIEW_CSP = [
  "default-src 'none'",
  "base-uri 'none'",
  "connect-src 'none'",
  'font-src data:',
  "form-action 'none'",
  "frame-src 'none'",
  'img-src data: blob:',
  'media-src data: blob:',
  "object-src 'none'",
  "script-src 'none'",
  "style-src 'unsafe-inline'",
].join('; ');

const PREVIEW_STYLE = `
  :root { color-scheme: light; font-family: Arial, Helvetica, sans-serif; }
  * { box-sizing: border-box; }
  html, body { margin: 0; min-height: 100%; background: #fff; color: #231f20; }
  body { padding: 24px; overflow-wrap: anywhere; }
  img, video, canvas, audio, iframe, embed, object { max-width: 100%; }
  table { border-collapse: collapse; max-width: 100%; }
  td, th { border: 1px solid #d9dadd; padding: 6px; }
  pre { max-width: 100%; overflow: auto; white-space: pre-wrap; }
  a, button, form, input, select, textarea { pointer-events: none !important; }
  .widget-placeholder { max-width: 100%; }
`;

const REMOVED_PREVIEW_ELEMENTS = [
  'embed',
  'form',
  'iframe',
  'noscript',
  'object',
  'script',
].join(',');

const REMOVED_PREVIEW_ATTRIBUTES = new Set([
  'action',
  'data',
  'formaction',
  'href',
  'ping',
  'poster',
  'src',
  'srcset',
  'xlink:href',
]);

const NOOP = () => undefined;

function contentFingerprint(value: string): string {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `${value.length}-${(hash >>> 0).toString(36)}`;
}

/**
 * Creates an inert document for a sandboxed srcDoc iframe. DOMParser does not
 * execute lesson scripts. The iframe sandbox and CSP remain the security
 * boundaries; removing navigation metadata is defense in depth.
 */
export function buildSanitizeReviewPreview(html: string): string {
  const document = new DOMParser().parseFromString(html || '', 'text/html');

  document
    .querySelectorAll(`base,${REMOVED_PREVIEW_ELEMENTS}`)
    .forEach((element) => element.remove());
  document.querySelectorAll('meta[http-equiv]').forEach((element) => {
    if (element.getAttribute('http-equiv')?.toLowerCase() === 'refresh') {
      element.remove();
    }
  });

  document.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const attributeName = attribute.name.toLowerCase();
      if (
        attributeName.startsWith('on') ||
        REMOVED_PREVIEW_ATTRIBUTES.has(attributeName)
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  const charset = document.createElement('meta');
  charset.setAttribute('charset', 'utf-8');

  const viewport = document.createElement('meta');
  viewport.setAttribute('content', 'width=device-width, initial-scale=1');
  viewport.setAttribute('name', 'viewport');

  const contentSecurityPolicy = document.createElement('meta');
  contentSecurityPolicy.setAttribute('content', PREVIEW_CSP);
  contentSecurityPolicy.setAttribute('http-equiv', 'Content-Security-Policy');

  const style = document.createElement('style');
  style.textContent = PREVIEW_STYLE;

  document.head.prepend(charset, viewport, contentSecurityPolicy);
  document.head.append(style);
  document.body.classList.add('fr-view', 'sanitize-review-preview');
  document.body.setAttribute('dir', 'auto');

  return `<!doctype html>${document.documentElement.outerHTML}`;
}

/**
 * Keeps normal lesson and widget markup intact for LessonView while removing
 * executable document-level content. The exact source remains available in
 * raw mode.
 */
export function buildLessonViewReviewHtml(html: string): string {
  const document = new DOMParser().parseFromString(html || '', 'text/html');

  document
    .querySelectorAll(`base,${REMOVED_PREVIEW_ELEMENTS}`)
    .forEach((element) => element.remove());
  document.querySelectorAll('meta[http-equiv]').forEach((element) => {
    if (element.getAttribute('http-equiv')?.toLowerCase() === 'refresh') {
      element.remove();
    }
  });
  document.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      if (
        name.startsWith('on') ||
        ['action', 'formaction', 'ping'].includes(name) ||
        (['href', 'src', 'xlink:href'].includes(name) &&
          value.startsWith('javascript:'))
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return document.body.innerHTML;
}

interface ComparisonPaneProps {
  html: string;
  label: string;
  lessonId: number;
  lessonLabel: string;
  mode: SanitizeReviewViewMode;
  tone: 'source' | 'sanitized';
}

const ComparisonPane = ({
  html,
  label,
  lessonId,
  lessonLabel,
  mode,
  tone,
}: ComparisonPaneProps) => {
  const { t } = useTranslation();
  const lessonHtml = useMemo(() => buildLessonViewReviewHtml(html), [html]);
  const editorKey = useMemo(
    () =>
      `sanitize-review-${tone}-${lessonId}-${contentFingerprint(lessonHtml)}`,
    [lessonHtml, lessonId, tone]
  );

  return (
    <section className="min-w-0 overflow-hidden rounded-lg border border-grey-03 bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-grey-02 px-4 py-3">
        <h3 className="truncate text-sm font-semibold text-grey-09">{label}</h3>
        <Tag
          size="sm"
          title={label}
          variant={tone === 'source' ? 'warning' : 'success'}
        />
      </header>

      {mode === 'preview' ? (
        <div className="h-[60vh] min-h-[32rem] overflow-auto bg-white p-6 [&_a]:pointer-events-none [&_button]:pointer-events-none [&_form]:pointer-events-none [&_input]:pointer-events-none [&_select]:pointer-events-none [&_textarea]:pointer-events-none">
          <div className="fr-view mx-auto w-full max-w-2xl">
            <LessonView
              content={lessonHtml}
              node={{ lessonId, name: lessonLabel }}
              pageBreak={false}
              setNrOfPages={NOOP}
            />
          </div>
        </div>
      ) : mode === 'editor' ? (
        <div
          aria-label={`${label} — ${lessonLabel}`}
          className="h-[60vh] min-h-[32rem] overflow-auto bg-grey-01 p-3 [&_.widget-placeholder]:pointer-events-none [&_a]:pointer-events-none [&_button]:pointer-events-none [&_input]:pointer-events-none [&_select]:pointer-events-none [&_textarea]:pointer-events-none"
        >
          <p className="mb-3 rounded-md border border-grey-02 bg-white px-3 py-2 text-xs text-grey-06">
            {t('pages.sanitize_review.editor_read_only')}
          </p>
          <FroalaTextAreaEdit
            blockSaveOnUnmount
            className="w-full overflow-hidden bg-white"
            key={editorKey}
            lessonId={`${tone}-${lessonId}`}
            mode="edit"
            modus="lessonEdit"
            reviewOnly
            style={{ minHeight: '28rem' }}
            value={lessonHtml}
          />
        </div>
      ) : (
        <pre
          aria-label={`${label} — ${lessonLabel}`}
          className="font-mono h-[60vh] min-h-[32rem] overflow-auto whitespace-pre-wrap break-words bg-grey-01 p-4 text-xs leading-5 text-grey-09"
          tabIndex={0}
        >
          {html}
        </pre>
      )}
    </section>
  );
};

interface LessonComparisonProps {
  lessonId: number;
  lessonLabel: string;
  mode: SanitizeReviewViewMode;
  onModeChange: (mode: SanitizeReviewViewMode) => void;
  sanitizedHtml: string;
  sourceHtml: string;
}

const LessonComparison = ({
  lessonId,
  lessonLabel,
  mode,
  onModeChange,
  sanitizedHtml,
  sourceHtml,
}: LessonComparisonProps) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-grey-09">{lessonLabel}</h2>
        <div
          aria-label={t('pages.sanitize_review.view_mode')}
          className="inline-flex rounded-lg border border-grey-03 bg-white p-1"
          role="group"
        >
          {(['preview', 'raw', 'editor'] as const).map((option) => (
            <button
              aria-pressed={mode === option}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                mode === option
                  ? 'bg-primary-02 text-grey-09'
                  : 'text-grey-06 hover:bg-grey-01'
              }`}
              key={option}
              onClick={() => onModeChange(option)}
              type="button"
            >
              {t(`pages.sanitize_review.${option}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
        <ComparisonPane
          html={sourceHtml}
          label={t('pages.sanitize_review.production')}
          lessonId={lessonId}
          lessonLabel={lessonLabel}
          mode={mode}
          tone="source"
        />
        <ComparisonPane
          html={sanitizedHtml}
          label={t('pages.sanitize_review.sanitized')}
          lessonId={lessonId}
          lessonLabel={lessonLabel}
          mode={mode}
          tone="sanitized"
        />
      </div>
    </div>
  );
};

export default LessonComparison;
